import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, ShieldCheck, ShieldOff, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { API } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useCrypto } from '../../context/CryptoContext';
import { useJazyk } from '../../context/LanguageContext';
import { prebalNovymHeslem, otiskHesla, novaSulProPrihlaseni, jePodporovano } from '../../lib/crypto';
import TwoFactorPanel from './TwoFactorPanel';

/**
 * Bezpečnost účtu: změna hesla a stav šifrování zpráv.
 *
 * Změna hesla je tady kvůli šifrování to nejcitlivější místo. Klíč ke zprávám
 * je zamčený heslem, takže se při změně musí **přebalit** — a to jde jedině
 * tady v prohlížeči, protože jedině tady jsou obě hesla naráz. Kdyby se to
 * neudělalo, člověk by se přihlásil novým heslem a zprávy by nikdy nepřečetl.
 */
export default function SecuritySection() {
  const { user } = useAuth();
  const emailUctu = user?.email || '';
  const { stav, zapnuto, odemceno } = useCrypto();
  const { t } = useJazyk();
  const [stareHeslo, setStareHeslo] = useState('');
  const [noveHeslo, setNoveHeslo] = useState('');
  const [potvrzeni, setPotvrzeni] = useState('');
  const [pracuje, setPracuje] = useState(false);

  const zmenHeslo = async (e) => {
    e.preventDefault();
    if (noveHeslo !== potvrzeni) { toast.error(t('bezp.hesloNeshoda')); return; }
    if (!stareHeslo || !noveHeslo) { toast.error(t('bezp.vyplnObe')); return; }

    setPracuje(true);
    try {
      // Nejdřív klíč. Kdyby se nejdřív změnilo heslo a přebalení pak selhalo,
      // člověk by přišel o zprávy — a to už se vrátit nedá.
      let prebaleny = null;
      if (zapnuto) {
        const { data } = await axios.get(`${API}/crypto/keys/me`);
        if (data.bundle) {
          prebaleny = await prebalNovymHeslem(data.bundle, stareHeslo, noveHeslo);
          if (!prebaleny) {
            toast.error(t('bezp.stareNesedi'));
            return;
          }
        }
      }

      // Server hesla nevidí, takže mu i tady posíláme jen otisky. Nová sůl
      // se ukládá spolu s novým otiskem, jinak by se příště nespočítal stejně.
      const { data: solInfo } = await axios.get(`${API}/auth/login-salt`, {
        params: { email: emailUctu },
      });
      const novaSul = novaSulProPrihlaseni();
      await axios.post(`${API}/auth/change-password`, solInfo.auth_version === 2 && jePodporovano()
        ? {
          current_password: await otiskHesla(stareHeslo, solInfo.auth_salt),
          new_password: await otiskHesla(noveHeslo, novaSul),
          auth_salt: novaSul,
        }
        : { current_password: stareHeslo, new_password: noveHeslo });

      if (prebaleny) {
        await axios.put(`${API}/crypto/keys/me`, prebaleny);
      }

      setStareHeslo(''); setNoveHeslo(''); setPotvrzeni('');
      toast.success(t(prebaleny ? 'bezp.zmenenoSKlicem' : 'bezp.zmeneno'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('bezp.zmenaChyba'));
    } finally {
      setPracuje(false);
    }
  };

  const zahodKlice = async () => {
    if (!window.confirm(
      t('bezp.zahoditPotvrzeni'))) return;
    try {
      await axios.delete(`${API}/crypto/keys/me`);
      toast.success(t('bezp.klicSmazan'));
    } catch {
      toast.error(t('bezp.nepovedlo'));
    }
  };

  return (
    <Card className="mt-4" data-testid="security-section">
      <CardContent className="p-4 sm:p-5 space-y-5">
        <div>
          <h3 className="font-heading font-bold text-bloom-text flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-bloom-violet" />
            {t('bezp.titulek')}
          </h3>
        </div>

        {/* Stav šifrování */}
        <div className="flex items-start gap-2.5 text-sm">
          {zapnuto
            ? <ShieldCheck className="w-4 h-4 text-bloom-mint shrink-0 mt-0.5" />
            : <ShieldOff className="w-4 h-4 text-bloom-sub shrink-0 mt-0.5" />}
          <div>
            <p className="text-bloom-text">
              {zapnuto
                ? t('bezp.sifrovaniZapnuto', { odemceno })
                : t('bezp.sifrovaniVypnuto')}
            </p>
            <p className="text-xs text-bloom-sub mt-0.5">
              {zapnuto ? t('bezp.sifrovaniKde') : t('bezp.sifrovaniKdeVypnute')}
            </p>
          </div>
        </div>

        <TwoFactorPanel />

        {/* Změna hesla */}
        <form onSubmit={zmenHeslo} className="space-y-3 border-t border-border/50 pt-4">
          <p className="font-medium text-bloom-text text-sm">{t('bezp.zmenaHesla')}</p>

          {zapnuto && (
            <p className="text-xs text-bloom-sub bg-bloom-violet/5 border border-bloom-violet/15 rounded-lg px-3 py-2 leading-relaxed">
              {t('bezp.zmenaHeslaSifrovani')}
            </p>
          )}

          <div>
            <Label className="text-sm">{t('bezp.stavajiciHeslo')}</Label>
            <Input type="password" value={stareHeslo} autoComplete="current-password"
                   onChange={(e) => setStareHeslo(e.target.value)}
                   className="mt-1" data-testid="stare-heslo" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">{t('bezp.noveHeslo')}</Label>
              <Input type="password" value={noveHeslo} autoComplete="new-password"
                     onChange={(e) => setNoveHeslo(e.target.value)}
                     className="mt-1" data-testid="nove-heslo" />
            </div>
            <div>
              <Label className="text-sm">{t('bezp.noveHesloZnovu')}</Label>
              <Input type="password" value={potvrzeni} autoComplete="new-password"
                     onChange={(e) => setPotvrzeni(e.target.value)}
                     className="mt-1" data-testid="nove-heslo-znovu" />
            </div>
          </div>
          <p className="text-xs text-bloom-sub">
            {t('bezp.pozadavky')}
          </p>
          <Button type="submit" disabled={pracuje}
                  className="bg-bloom-violet hover:bg-bloom-violet/90" data-testid="zmenit-heslo">
            {pracuje ? t('bezp.menim') : t('bezp.zmenitHeslo')}
          </Button>
        </form>

        {/* Poslední záchrana, když si člověk heslo ke klíči nepamatuje */}
        {zapnuto && !odemceno && (
          <div className="border-t border-border/50 pt-4">
            <p className="text-sm text-bloom-text flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              {t('bezp.nepamatujes')}
            </p>
            <p className="mt-1 text-xs text-bloom-sub leading-relaxed">
              {t('bezp.zahoditPopis')}
            </p>
            <Button variant="outline" onClick={zahodKlice}
                    className="mt-2 text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="zahodit-klice">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />{t('bezp.zahodit')}
            </Button>
          </div>
        )}

        {stav === 'nepodporovano' && (
          <p className="text-xs text-amber-700 border-t border-border/50 pt-4">
            {t('bezp.prohlizecNeumi')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
